import { Field, IconButton, Modal, Text, TextInput } from "@carbon6/c6-frontend-design";
import { useState, type ChangeEvent, type ReactNode, type SyntheticEvent } from "react";
import { EXPERIMENT_NOTE_MAX_LENGTH } from "@/api";
import { hatcheryTextModes } from "@/app/theme";
import { Editor, useEditorController } from "@/components/editor";
import { HatModalHeader } from "@/components/modal";
import styles from "./CreateExperimentDialog.module.css";

type CreateExperimentDialogProps = {
  submitting: boolean;
  errorMessage: string | null;
  onClose: () => void;
  onSubmit: (values: { name: string; description: string }) => void;
};

// submit 흐름에서 새 experiment 를 만드는 모달. 이름과 실험 노트(description)를 받는다.
// 사용자는 training UI 를 떠나지 않고 생성한다. 제출 상태(submitting/error)는 부모(controller)가 준다.
export function CreateExperimentDialog({
  submitting,
  errorMessage,
  onClose,
  onSubmit,
}: CreateExperimentDialogProps): ReactNode {
  const [name, setName] = useState("");
  const descriptionEditor = useEditorController({ value: "", disabled: submitting });

  function handleSubmit(event: SyntheticEvent<HTMLFormElement, SubmitEvent>): void {
    event.preventDefault();
    if (submitting || name.trim() === "") {
      return;
    }
    onSubmit({ name, description: descriptionEditor.value });
  }

  return (
    <Modal
      open
      onClose={onClose}
      size="md"
      closeOnBackdropClick={!submitting}
      closeOnEscape={!submitting}
      aria-labelledby="create-experiment-dialog-title"
    >
      <HatModalHeader
        title="새 실험을 만듭니다."
        titleId="create-experiment-dialog-title"
        close={{ disabled: submitting }}
      />
      <form className={styles.dialogForm} onSubmit={handleSubmit}>
        <Field label="실험 이름" layout="stack" disabled={submitting}>
          <TextInput
            value={name}
            maxLength={255}
            required
            placeholder="실험 이름을 입력해 주세요."
            onChange={(event: ChangeEvent<HTMLInputElement>) => setName(event.target.value)}
          />
        </Field>
        {/* 노트는 나중에 실험 섹션에서 다시 고칠 수 있다. 한 줄 입력에서는 1024자를 읽기
            어려우므로 여러 줄로 받는다. c6 에 멀티라인 입력이 없어서 native textarea 를 쓴다(앱 관례). */}
        <Field label="실험 노트 (선택)" layout="stack" disabled={submitting}>
          <Editor
            controller={descriptionEditor}
            className={styles.noteInput}
            maxLength={EXPERIMENT_NOTE_MAX_LENGTH}
            rows={4}
            placeholder="이 실험의 목적이나 메모를 입력해 주세요."
          />
        </Field>
        {errorMessage ? (
          <Text variant={hatcheryTextModes["body-normal"]} className={styles.error} role="alert">
            {errorMessage}
          </Text>
        ) : null}
        <div className={styles.actions}>
          <IconButton
            text="취소"
            type="button"
            variant="surface"
            disabled={submitting}
            onClick={onClose}
          />
          <IconButton
            text={submitting ? "생성 중…" : "생성"}
            type="submit"
            variant="solid"
            disabled={submitting || name.trim() === ""}
          />
        </div>
      </form>
    </Modal>
  );
}
